import { logger } from "../../../datadog/logger";

export const audioStreams: MediaStream[] = [];

/**
 * Monkey-patches `getUserMedia()` to capture audio MediaStreams created by the Zoom SDK,
 * which doesn't expose them through its public API. This allows us to perform audio analysis
 * (e.g., volume monitoring) on the raw streams. Call once before Zoom SDK initialization.
 *
 * This approach is brittle as it depends on Zoom using the standard `getUserMedia()` API
 * internally. However, this is unlikely to break since `getUserMedia()` is the only standard
 * browser API for microphone access and browser conventions are stable. If Zoom's implementation
 * changes, the SDK would still function but we'd lose access to streams for analysis.
 */
export function interceptUserMedia(navigator: Navigator) {
  const originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(
    navigator.mediaDevices,
  );

  navigator.mediaDevices.getUserMedia = async function (
    constraints?: MediaStreamConstraints,
  ): Promise<MediaStream> {
    const stream = await originalGetUserMedia(constraints);

    // check if audio was requested in the constraints
    const audioRequested =
      constraints?.audio !== undefined && constraints.audio !== false;

    if (!audioRequested) {
      return stream;
    }

    // intercept the stream
    audioStreams.push(stream);

    // set up cleanup when audio tracks end
    const audioTracks = stream.getAudioTracks();
    const cleanupStream = () => {
      const allInactive = audioTracks.every(
        (track) => track.readyState === "ended",
      );
      if (allInactive) {
        const index = audioStreams.indexOf(stream);
        if (index !== -1) {
          audioStreams.splice(index, 1);
        }
      }
    };

    // listen to each audio track's ended event
    audioTracks.forEach((track) => {
      track.addEventListener("ended", cleanupStream);
    });

    return stream;
  };

  logger.info("getUserMedia has been monkey-patched.");
}
